// sceneLookNodes — turn the live viewport look into event-graph nodes, so it
// travels with the CHARACTER instead of with the browser.
//
// The problem (Isaac, 2026-09-01): "no way to save the settings for BG color or
// Camera without tying it to a heavy GSplat — I want to be able to just save my
// camera and bg color settings for a given character without having to load a
// GSplat environment."
//
// Both halves of that were true. The backdrop and camera can be EDITED in two
// places already (LiveTunePanel's BACKDROP/CAMERA sections and Settings ▸
// Scene), but both write `liveTuneStore`, which persists to the
// `aos-livetune-v4` localStorage key — per BROWSER. Nothing set there travels
// with the character or reaches a teammate. The one server-side save that
// exists ("Save tune for everyone") writes `environment_assets.default_tune`,
// which is precisely why it needs an environment row.
//
// The store this uses instead is the character's own EVENT BLUEPRINT. It is
// already per-character, already versioned (blueprint history), already loaded
// at boot, and already has a defined precedence against the panel
// (setCameraFromBlueprint filters out anything the user touched this session,
// so URL > panel > blueprint > default). No AAM migration, no new column, and —
// the point — no environment anywhere in the path.
//
// Shape follows `cam_group_orbit` in public/blueprints/conversational.json: ONE
// `graphGroup` holding its own entry event and a straight exec chain. Grouping
// matters beyond tidiness — `graphGroup.collectGroupStarters` discovers inner
// event nodes one level deep and runs them against their own innerNodes pool,
// so the whole look is one collapsible node in the editor rather than seven
// loose ones the user has to recognise as a set.
//
// Pure, and its own module, so `node --test` can drive it: EventGraph.jsx and
// SettingsWorkspace.jsx are both unreachable there.

/** Fixed ids, because this group is a SINGLETON per character. Re-saving must
 *  REPLACE the previous look, not stack a second group that also fires — two
 *  Scene Look groups would both run on onConstruct and the winner would be
 *  whichever the pool happened to walk last. */
export const SCENE_LOOK_GROUP_ID = 'scene_look'
export const SCENE_LOOK_NAME = 'Scene Look'
const ENTRY_ID = 'look_entry'

const clone = (v) => (v === null || typeof v !== 'object' ? v : JSON.parse(JSON.stringify(v)))

function node(id, type, data, x, y) {
  return { id, type, position: { x, y }, data: clone(data) }
}

/**
 * Build the Scene Look group from a liveTuneStore snapshot.
 *
 * The camera fields are the same set envTune shares, minus `blend`: blend is the
 * in-flight ease duration of whatever move is CURRENTLY happening — transient
 * per-edit state, not a setting — and baking it in would make every boot re-play
 * someone's last transition.
 *
 * @param {object} tune  `{ backdrop, camera }` — a liveTuneStore state (or the
 *   subset). A missing slice contributes no node, so a caller cannot
 *   accidentally write a default over a value the character already had.
 * @param {{x?: number, y?: number}} [position] where the group sits on canvas.
 * @returns {object|null} the graphGroup node, or null when there is nothing to
 *   save (both slices absent) — null is "write nothing", never an empty group.
 */
export function buildSceneLookGroup(tune, position = {}) {
  const backdrop = tune?.backdrop ?? null
  const camera = tune?.camera ?? null
  if (!backdrop && !camera) return null

  const innerNodes = [node(ENTRY_ID, 'onConstruct', {}, 0, 0)]
  let y = 0
  const push = (id, type, data) => { innerNodes.push(node(id, type, data, 320, y)); y += 140 }

  if (backdrop) {
    push('look_backdrop', 'setBackdrop', {
      skyEnabled: !!backdrop.skyEnabled,
      skyColor: backdrop.skyColor,
      groundEnabled: !!backdrop.groundEnabled,
      groundColor: backdrop.groundColor,
      groundSize: Number(backdrop.groundSize),
      hideEnvironment: !!backdrop.hideEnvironment,
    })
  }

  if (camera) {
    if (camera.fov !== undefined) {
      push('look_fov', 'setCameraFov', { fov: Number(camera.fov), blendMs: 0 })
    }
    // 'fixed' means "nobody authors the target" — the rig or a pan owns it.
    // Saving a setCameraTarget node for it would convert an absence of intent
    // into an instruction, so a fixed target writes no node at all.
    const t = camera.target
    if (t && t.mode && t.mode !== 'fixed') {
      const v = Array.isArray(t.value) ? t.value : [0, 1.5, 0]
      push('look_target', 'setCameraTarget', {
        mode: t.mode, x: Number(v[0]), y: Number(v[1]), z: Number(v[2]), blendMs: 0,
      })
    }
    if (camera.cage) {
      const c = camera.cage
      push('look_cage', 'setCameraCage', {
        enabled: !!c.enabled,
        hx: Number(c.hx), hy: Number(c.hy), hz: Number(c.hz),
        margin: Number(c.margin),
      })
    }
    if (camera.seeThrough) {
      const s = camera.seeThrough
      push('look_seethrough', 'setCameraSeeThrough', {
        enabled: !!s.enabled, radius: Number(s.radius), fade: Number(s.fade),
        auto: !!s.auto, autoNear: Number(s.autoNear), autoBelow: s.autoBelow !== false,
      })
    }
    if (camera.follow) {
      const f = camera.follow
      const o = Array.isArray(f.offset) ? f.offset : [0, 1.6, 3]
      push('look_follow', 'setCameraFollow', {
        enabled: !!f.enabled, x: Number(o[0]), y: Number(o[1]), z: Number(o[2]),
        space: f.space === 'world' ? 'world' : 'local',
        damping: Number(f.damping), targetOffsetY: Number(f.targetOffsetY),
      })
    }
  }

  // One straight exec chain from the entry through every setter, in build order.
  // Edge ids are derived from the pair, so rebuilding the same look produces
  // byte-identical edges — a save that changed nothing must not dirty the graph.
  const innerEdges = []
  for (let i = 0; i < innerNodes.length - 1; i++) {
    const source = innerNodes[i].id
    const target = innerNodes[i + 1].id
    innerEdges.push({
      id: `ee_${source}__${target}`,
      source, sourceHandle: 'execOut',
      target, targetHandle: 'execIn',
    })
  }

  return {
    id: SCENE_LOOK_GROUP_ID,
    type: 'graphGroup',
    position: { x: position.x ?? 0, y: position.y ?? 600 },
    data: { name: SCENE_LOOK_NAME, innerNodes, innerEdges },
  }
}

/**
 * Replace (or append) the Scene Look group in an event-node list.
 *
 * Returns a NEW array — the caller assigns it into eventGraphState and fires the
 * change notify. Never mutates, because the editor holds the old array as React
 * state and an in-place splice would not re-render.
 *
 * `group` null REMOVES the group, which is what "Clear saved look" does: the
 * character goes back to booting with whatever the panel and defaults give it.
 */
export function withSceneLookGroup(nodes, group) {
  const rest = (Array.isArray(nodes) ? nodes : []).filter(n => n?.id !== SCENE_LOOK_GROUP_ID)
  return group ? [...rest, group] : rest
}

/** The saved look currently in a graph, or null. Read by the launcher's
 *  Properties summary, which has no viewport and cannot ask the live store. */
export function findSceneLookGroup(nodes) {
  return (Array.isArray(nodes) ? nodes : []).find(n => n?.id === SCENE_LOOK_GROUP_ID) ?? null
}

/** The backdrop fields a look carries, read back off its node. Null when the
 *  look has no backdrop node (a camera-only look). */
export function readBackdrop(group) {
  const n = (group?.data?.innerNodes ?? []).find(x => x.type === 'setBackdrop')
  return n ? { ...n.data } : null
}

/**
 * Patch ONLY the backdrop of an existing look, leaving every camera node alone.
 *
 * The launcher edits a colour on a character that is NOT loaded, so there is no
 * live camera to re-capture — rebuilding the whole group from a tune snapshot
 * would either drop the saved camera or invent one. This touches the one node.
 *
 * A look with no backdrop node yet grows one (appended, and re-chained), and a
 * character with no look at all gets a backdrop-only group — which is exactly
 * what `buildSceneLookGroup({backdrop})` already produces.
 *
 * @param {object|null} group  the existing Scene Look group, or null
 * @param {object} patch       backdrop fields to merge
 * @returns {object} a NEW group (never mutates — same reason as withSceneLookGroup)
 */
export function withBackdropPatch(group, patch) {
  const existing = readBackdrop(group)
  const backdrop = { ...(existing ?? {}), ...patch }
  if (!group) return buildSceneLookGroup({ backdrop })

  const innerNodes = (group.data?.innerNodes ?? []).map(n => (
    n.type === 'setBackdrop' ? { ...n, data: { ...n.data, ...patch } } : { ...n, data: { ...n.data } }
  ))
  if (!existing) {
    // No backdrop node yet: append it and re-chain, so it is reachable. An
    // appended node with no exec edge is saved and never fires.
    const last = innerNodes[innerNodes.length - 1]
    const added = node('look_backdrop', 'setBackdrop', backdrop, 320, (last?.position?.y ?? 0) + 140)
    innerNodes.push(added)
    return {
      ...group,
      data: {
        ...group.data,
        innerNodes,
        innerEdges: [
          ...(group.data?.innerEdges ?? []),
          { id: `ee_${last.id}__${added.id}`, source: last.id, sourceHandle: 'execOut', target: added.id, targetHandle: 'execIn' },
        ],
      },
    }
  }
  return { ...group, data: { ...group.data, innerNodes, innerEdges: [...(group.data?.innerEdges ?? [])] } }
}

/**
 * A one-line human summary of a saved look, for a surface with no viewport.
 * Reads the NODES, not the live store — the launcher never loads the character.
 * Returns null when there is no saved look, which the caller renders as its own
 * empty state rather than as an empty string.
 */
export function describeSceneLook(group) {
  if (!group) return null
  const by = {}
  for (const n of group.data?.innerNodes ?? []) by[n.type] = n.data ?? {}
  const parts = []
  const b = by.setBackdrop
  if (b) {
    parts.push(b.skyEnabled ? `background ${b.skyColor}` : 'background off')
    parts.push(b.groundEnabled ? `ground ${b.groundColor}` : 'ground off')
    if (b.hideEnvironment) parts.push('environment hidden')
  }
  if (by.setCameraFov) parts.push(`FOV ${by.setCameraFov.fov}`)
  if (by.setCameraTarget) {
    const t = by.setCameraTarget
    parts.push(t.mode === 'head' ? 'framed on head' : `framed on ${t.x}, ${t.y}, ${t.z}`)
  }
  if (by.setCameraCage?.enabled) parts.push('cage on')
  if (by.setCameraFollow?.enabled) parts.push('follow on')
  return parts.length ? parts.join(' · ') : null
}
